import { useEffect, useRef } from "react";
import { toast, type ToastOptions } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CallDisabledIcon,
  MicOff01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize } from "shared/components";
import { cx } from "shared/utils";
import { useSoftphone, type SoftphoneErrorAction } from "./SoftphoneProvider";

const DISCONNECTED_TOAST_ID = "SOFTPHONE_DISCONNECTED";
const MIC_DENIED_TOAST_ID = "SOFTPHONE_MIC_DENIED";

// Reconnecting can't fix a stale build (only a reload can) and can't switch
// calling on for an account, so the affordance follows the fault.
const ACTION_LABELS: Record<SoftphoneErrorAction, string | undefined> = {
  reconnect: "Reconnect",
  reload: "Refresh",
  none: undefined
};

// Persistent, manually dismissable toast styled after the app's standard
// action toast (see TaskTable's "Task deleted / Undo"). Rendered through the
// global ToastContainer so it inherits the design-system look instead of the
// old ad-hoc fixed banner (CRM-1073).
const PERSISTENT_TOAST_OPTIONS: ToastOptions = {
  autoClose: false,
  closeOnClick: false,
  closeButton: true,
  icon: false,
  className: "!bg-background-1000 border border-alpha-100 shadow-lg rounded-lg",
  bodyClassName: "p-2"
};

const SoftphoneToastBody = ({
  icon,
  accentClassName,
  title,
  body,
  actionLabel,
  onAction
}: {
  icon: typeof CallDisabledIcon;
  accentClassName: string;
  title: string;
  body: string;
  // Omitted when there is nothing useful to offer — a disabled account can't be
  // fixed from here, and a button that never works trains people to ignore it.
  actionLabel?: string;
  onAction?: () => void;
}) => (
  <div className="flex w-full items-center justify-between gap-4">
    <div className="flex items-start gap-2">
      <HugeiconsIcon
        icon={icon}
        size={20}
        className={cx("mt-0.5 shrink-0", accentClassName)}
      />
      <div>
        <p className="text-base font-bold text-content-1000">{title}</p>
        <p className="mt-0.5 text-sm text-content-500">{body}</p>
      </div>
    </div>
    {actionLabel && onAction ? (
      <Button size={ButtonSize.SM} onClick={onAction}>
        {actionLabel}
      </Button>
    ) : null}
  </div>
);

/**
 * Surfaces the softphone's terminal device states ("error", "mic-denied") as
 * persistent toasts with a recovery action. Mounted once by CallOverlay; renders
 * nothing itself — the global ToastContainer owns the presentation.
 */
export function SoftphoneToasts() {
  const { status, errorMessage, errorAction, retryRegistration } =
    useSoftphone();

  // What the disconnected toast last rendered, so an unrelated context change
  // (mute tick, incoming ring) doesn't force a pointless toast.update.
  const lastErrorContentRef = useRef<string | null>(null);

  useEffect(() => {
    if (status !== "error") {
      toast.dismiss(DISCONNECTED_TOAST_ID);
      lastErrorContentRef.current = null;
    }
    if (status !== "mic-denied") toast.dismiss(MIC_DENIED_TOAST_ID);

    if (status === "error") {
      const title =
        errorAction === "reload"
          ? "Calling needs a refresh"
          : "Voice service disconnected";
      const body = errorMessage ?? "The softphone lost its connection.";
      const render = ({ closeToast }: { closeToast: () => void }) => (
        <SoftphoneToastBody
          icon={CallDisabledIcon}
          accentClassName="text-danger-600"
          title={title}
          body={body}
          actionLabel={ACTION_LABELS[errorAction]}
          onAction={
            errorAction === "none"
              ? undefined
              : () => {
                  if (errorAction === "reload") {
                    window.location.reload();
                    closeToast();
                    return;
                  }
                  // Deliberately NOT closing the toast: a failed rebuild lands
                  // on the identical error state, React bails out on the
                  // equal values, and nothing would ever re-raise it — the
                  // user would take the silence for success. It auto-dismisses
                  // when the device actually registers.
                  retryRegistration();
                }
          }
        />
      );
      const options = {
        ...PERSISTENT_TOAST_OPTIONS,
        toastId: DISCONNECTED_TOAST_ID
      };
      const content = `${title}|${body}|${errorAction}`;
      // The status stays "error" across a change of fault (a recoverable
      // outage that turns out to be a stale build, say), and react-toastify
      // silently drops a re-emit under a toastId that is already mounted — so
      // without the update the user would keep reading the old title and
      // clicking an action that cannot help. A new toastId is not an option:
      // dismissal is animation-deferred, so it would briefly stack two.
      if (toast.isActive(DISCONNECTED_TOAST_ID)) {
        if (lastErrorContentRef.current !== content)
          toast.update(DISCONNECTED_TOAST_ID, { ...options, render });
      } else toast(render, options);
      lastErrorContentRef.current = content;
    }

    if (status === "mic-denied")
      toast(
        ({ closeToast }) => (
          <SoftphoneToastBody
            icon={MicOff01Icon}
            accentClassName="text-warning-600"
            // Neutral title with the specifics in the body: "access is
            // required" would be a lie when the mic is merely held by another
            // app, and the provider's message carries the right instruction.
            title="Can't access your microphone"
            body={
              errorMessage ??
              "Click the lock icon next to the URL, allow microphone access, then retry."
            }
            actionLabel="Retry"
            onAction={() => {
              retryRegistration();
              closeToast();
            }}
          />
        ),
        { ...PERSISTENT_TOAST_OPTIONS, toastId: MIC_DENIED_TOAST_ID }
      );
  }, [status, errorMessage, errorAction, retryRegistration]);

  // Don't leave an orphaned toast behind if the whole overlay unmounts
  // (e.g. the softphone feature is toggled off).
  useEffect(
    () => () => {
      toast.dismiss(DISCONNECTED_TOAST_ID);
      toast.dismiss(MIC_DENIED_TOAST_ID);
    },
    []
  );

  return null;
}
